import { ChevronDown, Pencil, PencilOff, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import {
  type WorkflowStepUser,
  type WorkflowStepUserPermission,
  WorkflowStepUserPermissions,
} from "~/api/types";
import { printFullName } from "~/helpers/user";
import AvatarWithFallback from "./AvatarWithFallback";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import { UserForm } from "./WorkflowBuilderUserForm";
import { cn } from "~/lib/utils";
import { useUserContext } from "~/hooks/useUserContext";
import { ActionIndicator } from "./StepUserActionIndicator";

type Props = {
  users: WorkflowStepUser[];
  canBeEdited: boolean;
  onRemoveUser: (userId: number) => void;
  onEditUserPermission: (
    userId: number,
    permission: WorkflowStepUserPermission,
  ) => void;
  onAddUser: (user: WorkflowStepUser) => void;
  categoryId: number;
};

export function StepUsers({
  users,
  canBeEdited,
  onRemoveUser,
  onEditUserPermission,
  onAddUser,
  categoryId,
}: Props) {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();

  const mainContent =
    users.length === 0 ? (
      <div className="text-muted-foreground py-4 text-center text-sm italic">
        {t(($) => $.commons.workflow.noUsersAssigned)}
      </div>
    ) : (
      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
        {users.map((user, i) => (
          <div
            key={i}
            className={cn(
              "group flex items-center gap-2 rounded-lg border bg-white p-3 shadow-xs",
              { "text-muted-foreground opacity-70": !canBeEdited },
            )}
          >
            <div className="shrink-0">
              <AvatarWithFallback user={user} size="md" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <p className="truncate text-sm font-medium">
                  {printFullName(user, user.userId === currentUser.id)}
                </p>
                <ActionIndicator response={user.responseStatus} />
              </div>
              <div className="mt-0.5 flex items-center">
                {canBeEdited ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="min">
                        <UserPermission permission={user.permission} />
                        <ChevronDown />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={() =>
                          onEditUserPermission(
                            user.userId,
                            WorkflowStepUserPermissions.write,
                          )
                        }
                      >
                        {t(($) => $.commons.workflow.edit)}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          onEditUserPermission(
                            user.userId,
                            WorkflowStepUserPermissions.read,
                          )
                        }
                      >
                        {t(($) => $.commons.workflow.read)}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <UserPermission permission={user.permission} />
                )}
              </div>
            </div>
            {canBeEdited && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemoveUser(user.userId)}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">
                  {t(($) => $.commons.workflow.removeUser)}
                </span>
              </Button>
            )}
          </div>
        ))}
      </div>
    );

  return (
    <div className="bg-secondary/20 flex flex-col gap-2 rounded-lg border border-dashed p-2">
      {mainContent}
      {canBeEdited && (
        <UserForm
          selectedUsers={users}
          handleAddUser={onAddUser}
          categoryId={categoryId}
        />
      )}
    </div>
  );
}

const UserPermission = ({
  permission,
}: {
  permission: WorkflowStepUserPermission;
}) => {
  const { t } = useTranslation();
  const Icon =
    permission === WorkflowStepUserPermissions.write ? Pencil : PencilOff;
  const label =
    permission === WorkflowStepUserPermissions.write
      ? t(($) => $.commons.workflow.canEdit)
      : t(($) => $.commons.workflow.readOnly);

  return (
    <div className="text-muted-foreground flex items-center gap-1 text-xs">
      <Icon className="size-3" />
      {label}
    </div>
  );
};
